/**
 * The grant/revoke editor for one member.
 *
 * Each permission has three states, and they are not "on/off plus a default" —
 * they are *inherit*, *grant* and *revoke*, which is exactly the row shape of
 * `tenant_member_permissions`. Collapsing inherit and revoke into one "off"
 * would lose the difference between "a member does not get this" and "this
 * person specifically must not have this", and the second survives a promotion
 * while the first does not.
 */

import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { PERMISSIONS, ROLE_DEFAULT_PERMISSIONS } from '@app/common';
import type { Permission, PermissionOverride, TenantMemberSummary } from '@app/common';
import { Button } from '../../core/Button';
import { cn } from '../../../lib/utils';
import { useTranslation } from '../../../context/LanguageContext';

type OverrideState = 'inherit' | 'grant' | 'revoke';

function stateOf(overrides: readonly PermissionOverride[], permission: Permission): OverrideState {
  return overrides.find((o) => o.permission === permission)?.effect ?? 'inherit';
}

function toOverrides(states: Record<Permission, OverrideState>): PermissionOverride[] {
  return PERMISSIONS.filter((permission) => states[permission] !== 'inherit').map((permission) => ({
    permission,
    effect: states[permission] as 'grant' | 'revoke',
  }));
}

export function PermissionEditor({
  member,
  onSave,
  onCancel,
}: Readonly<{
  member: TenantMemberSummary;
  onSave: (overrides: PermissionOverride[]) => Promise<void>;
  onCancel: () => void;
}>) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [states, setStates] = useState<Record<Permission, OverrideState>>(() =>
    Object.fromEntries(
      PERMISSIONS.map((permission) => [permission, stateOf(member.overrides, permission)])
    ) as Record<Permission, OverrideState>
  );

  const roleDefaults = useMemo(
    () => new Set<Permission>(ROLE_DEFAULT_PERMISSIONS[member.role]),
    [member.role]
  );

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(toOverrides(states));
    } catch (err: unknown) {
      setError((err as { message?: string }).message ?? t('team.permissions.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3 border-t border-slate-100 bg-slate-50/60 p-4">
      <p className="text-xs text-slate-500">{t('team.permissions.help')}</p>

      <div className="grid gap-1.5 sm:grid-cols-2">
        {PERMISSIONS.map((permission) => {
          const inherited = roleDefaults.has(permission);
          const state = states[permission];

          return (
            <div
              key={permission}
              className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate font-mono text-[11px] text-slate-700">
                  {permission}
                </span>
                <span className="block text-[10px] text-slate-400">
                  {t(inherited ? 'team.permissions.byRole' : 'team.permissions.notByRole')}
                </span>
              </span>

              <div className="flex shrink-0 gap-0.5 rounded-lg bg-slate-100 p-0.5">
                {(['revoke', 'inherit', 'grant'] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={state === option}
                    onClick={() => setStates((prev) => ({ ...prev, [permission]: option }))}
                    className={cn(
                      'rounded-md px-1.5 py-0.5 text-[10px] font-semibold transition-colors',
                      state === option
                        ? {
                            revoke: 'bg-rose-500 text-white',
                            inherit: 'bg-white text-slate-600 shadow-sm',
                            grant: 'bg-emerald-500 text-white',
                          }[option]
                        : 'text-slate-400 hover:text-slate-600'
                    )}
                  >
                    {t(`team.permissions.${option}`)}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {error && <p className="text-xs font-medium text-rose-600">{error}</p>}

      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button size="sm" onClick={() => void save()} disabled={saving}>
          {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {t('common.save')}
        </Button>
      </div>
    </div>
  );
}
